'use client';

import React, { useCallback } from 'react';

import { useTranslations } from 'next-intl';

import BinanceIcon from '@/assets/icons/binance.svg';
import ArrowUpIcon from '@/assets/icons/chevron-arrow-up.svg';
import { P } from '@/components/shared/Typography';
import { useTickerWebSocket } from '@/hooks/useTickerWebSocket';
import { cn } from '@/lib/utils/cn';
import { formatDecimal } from '@/lib/utils/formatDecimal';

import type { I_MarketData } from '@/server/actions/market/getHistory.action';
import type { T_Symbol } from '@/ts/types/T_Chart';

interface I_TickerPriceDisplayProps {
  marketData: I_MarketData;
  symbol: T_Symbol | string;
  title?: string;
  isSticky?: boolean;
}

function TickerPriceDisplay({
  marketData,
  symbol,
  title,
  isSticky
}: I_TickerPriceDisplayProps) {
  const t = useTranslations('predictions');

  // Use real-time data if available, otherwise fall back to initial data
  const { tickerPrice } = useTickerWebSocket({
    symbol,
    enabled: true
  });
  const currentTickerPrice = tickerPrice || marketData.tickerPrice;

  const formatPrice = useCallback((price: string) => {
    const numPrice = parseFloat(price);
    if (numPrice >= 1) {
      return numPrice.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 5
      });
    } else {
      return formatDecimal(numPrice, 5);
    }
  }, []);

  const formatPriceChange = useCallback((priceChange: string) => {
    const numChange = parseFloat(priceChange);
    const sign = numChange >= 0 ? '+' : '';
    return `${sign}${formatDecimal(numChange, 5)}`;
  }, []);

  const formatSymbol = useCallback((symbol: string) => {
    return symbol.replace('USDT', '');
  }, []);

  const isPositiveChange = parseFloat(currentTickerPrice.priceChange) >= 0;

  // Display title if available, otherwise fallback to formatted symbol
  const displayText = title || formatSymbol(currentTickerPrice.symbol);

  return (
    <div
      className={cn(
        'bg-background flex flex-col w-full items-center gap-1 transition-all duration-300 ease-in-out',
        isSticky
          ? 'shadow-[0_2px_10px_var(--primary)] p-4 flex justify-center gap-1'
          : 'px-7 pt-6 pb-2.5'
      )}
    >
      {!isSticky && <P className="font-bold text-xl ">{displayText}</P>}
      <P
        className={cn(
          'font-bold text-xl',
          isPositiveChange ? 'text-primary' : 'text-destructive'
        )}
      >
        ${formatPrice(currentTickerPrice.price)}
      </P>
      <div className="flex items-center gap-1.5">
        <P
          className={cn(
            'text-sm font-bold',
            isPositiveChange ? 'text-primary' : 'text-destructive'
          )}
        >
          {formatPriceChange(currentTickerPrice.priceChange)}(
          {currentTickerPrice.priceChangePercent}%)
        </P>
        {isPositiveChange ? (
          <ArrowUpIcon className="text-primary" />
        ) : (
          <ArrowUpIcon className="text-destructive transform rotate-180" />
        )}
        <P className="text-muted-foreground text-sm font-semibold uppercase">
          {t('live')}
        </P>
      </div>
      {!isSticky && (
        <P icon={<BinanceIcon className="w-5 h-5" />}>
          {' '}
          {t('binanceCurrentPrice')}
        </P>
      )}
    </div>
  );
}

export default TickerPriceDisplay;
